import React from 'react';
import { OccupancyRule, OccupancyOperator } from '../../types';
import { OccupancyRuleRow } from './OccupancyRuleRow';
import { v4 as uuid } from 'uuid';
import styles from './OccupancyRulesSection.module.scss';

const allOperators: OccupancyOperator[] = ['greater_than', 'greater_than_or_equal', 'less_than', 'less_than_or_equal'];

interface OccupancyRulesSectionProps {
    rules: OccupancyRule[];
    onChange: (rules: OccupancyRule[]) => void;
    error?: string;
}

export const OccupancyRulesSection: React.FC<OccupancyRulesSectionProps> = ({ rules, onChange, error }) => {
    const handleAddRule = () => {
        const takenOperators = new Set(rules.map(r => r.operator));
        const nextOperator = allOperators.find(op => !takenOperators.has(op)) ?? 'greater_than';
        const newRule: OccupancyRule = {
            id: uuid(),
            operator: nextOperator,
            percentage: 75,
            action: 'increase',
            amount: 0,
            currency: 'USD'
        };
        onChange([...rules, newRule]);
    };

    const handleRuleChange = (updatedRule: OccupancyRule) => {
        onChange(rules.map(rule => (rule.id === updatedRule.id ? updatedRule : rule)));
    };

    const handleRuleRemove = (ruleId: string) => {
        onChange(rules.filter(rule => rule.id !== ruleId));
    };

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>Rate Rules</h3>
            <p className={styles.description}>Set your rate rule based on occupancy.</p>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.rulesContainer}>
                {rules.map(rule => (
                    <OccupancyRuleRow
                        key={rule.id}
                        rule={rule}
                        showDelete={rules.length > 1}
                        takenOperators={rules.filter(r => r.id !== rule.id).map(r => r.operator)}
                        onChange={handleRuleChange}
                        onRemove={handleRuleRemove}
                    />
                ))}
            </div>

            {rules.length < allOperators.length && (
                <button
                    type="button"
                    className={styles.addRuleButton}
                    onClick={handleAddRule}
                >
                    <span className={styles.addRuleIcon}>+</span>
                    Add Rule
                </button>
            )}
        </div>
    );
};
